import CacheOperations from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/board-cache-operations';
import PersistentBoardDataUtils
  from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/persistent-board-data-utils';

export interface WboBoardData {
  getAnnotationDataById(annotationId: string): any;
}

/**
 * Holds and stores all the annotations being made locally. This means that all annotations here are owned by the
 * current user.
 */
export default class PersistentBoardData implements WboBoardData {
  private readonly id: string;
  private readonly userName: string;
  private readonly boardGroupName: string;
  private readonly cacheOperations: CacheOperations = null;
  private data: any = {};

  constructor(id: string, userName: string);
  constructor(id: string, userName: string, data: any);
  constructor(id: string, userName: string, data?: any) {
    this.id = id;
    const boardNameParts = id.split('_');
    if (boardNameParts.length === 2) {
      this.boardGroupName = boardNameParts[0];
    }
    this.userName = userName;
    this.cacheOperations = CacheOperations.getInstance();
    if (data) {
      this.data = data;
    }

  }

  public setData(data: any) {
    this.data = data;
  }

  public async loadData() {
    const item = await this.cacheOperations.getItem(`${this.userName}_${this.id}`);
    if (item) {
      this.data = item;
    }
    return this;
  }


  public async setAnnotation(annotationId: string, annotation: any) {
    this.data[annotationId] = annotation;
    await this.save();
  }

  public updateAnnotation(annotationId: string, updatedAnnotation: any): void {
    let existingAnnotation = this.data[annotationId];
    for (let field in updatedAnnotation) {
      // We don't want to update type and tool fields
      if (field !== 'type' && field !== 'tool') {
        existingAnnotation[field] = updatedAnnotation[field];
      }
    }
  }

  public addChild(parentId: string, child: any): boolean {
    const parent = this.data[parentId];
    if (!parent) {
      return false;
    }
    if (Array.isArray(parent._children)) {
      parent._children.push(child);
    } else {
      parent._children = [child];
    }
    return true;
  }

  public getLatestAnnotationTimestamp(): number {
    return Object.keys(this.data)
      .map(k => this.data[k].time)
      .reduce((prevValue, curValue) => Math.max(prevValue, curValue), 0) || 0;
  }

  public async deleteAnnotation(annotationId: string | number) {
    delete this.data[annotationId];
    await this.save();
  }

  public async deleteAnnotations(annotations: string[]) {
    annotations.forEach((annotationId : string) => delete this.data[annotationId]);
    await this.save();
  }

  public deleteUserAnnotations(username: string, deleteAnonymous = false) {
    const deletedAnnotations = [];
    for (let annotationId in this.data) {
      if ((deleteAnonymous && !this.data[annotationId]?.userName) || this.data[annotationId]?.userName === username) {
        deletedAnnotations.push(annotationId);
        delete this.data[annotationId];
      }
    }
    this.save();
    return deletedAnnotations;
  }

  public async deleteBoard() {
    this.data = {};
    await this.cacheOperations.deleteItem(`${this.userName}_${this.id}`);
  }

  public async saveOperationId(operationId: string | number) {
    PersistentBoardDataUtils.saveOperationId(this.userName, this.boardGroupName, operationId);
  }

  public async markUnsaved() {
    localStorage.setItem(`${this.userName}_${this.boardGroupName}.isUnsaved`, 'true');
  }

  public async save() {
    await this.cacheOperations.setItem(`${this.userName}_${this.id}`, this.data);
  }

  getAnnotationDataById(annotationId: string) {
    if (this.data) {
      return this.data[annotationId];
    }
  }

  public getAnnotationData() {
    if (this.data) {
      return this.data;
    }
  }
}
